// Activity log (/activity-log) — the company-wide audit feed. Header over a
// toolbar (debounced search + ONE combined Filters dropdown with Module / User
// / Action / Date-range categories + a trailing entries count) and the common
// DataTable + TableFooter, all server-side via POST activity-logs/list (search
// + the module/action/userId id arrays + the created/createdStart/createdEnd
// date window). Still NO orderBy on the wire, so nothing sorts; the design's
// Export CSV button has no endpoint yet and ships only when it does (no inert
// affordances).
//
// Rows expand (accordion — one at a time) into the ActivityDetailPanel.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  DataTable,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  TableFooter,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useStickyTotal from '@/hooks/useStickyTotal';
import { useGetActivityLogsQuery, type ActivityLogAction } from '@/api/endpoints/activityLogApi';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import { FormDateFilter } from '@/api/endpoints/formsApi';
import {
  buildDateFilterOptions,
  DATE_ANY,
  dateFilterOf,
  dateRangeOf,
} from '@/utils/dateFilterPresets';
import { useActivityColumns } from './activityColumns';
import { ACTION_META, MODULE_SLUGS, categoryLabel } from './activityMeta';
import ActivityDetailPanel from './ActivityDetailPanel';

// Combined-dropdown category keys (rail order) + the empty selection shape.
const FILTER_KEYS = ['module', 'user', 'action', 'created'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

// The wire's createdStart/createdEnd are ISO datetimes (only sent with the
// 'custom' preset); the range inputs give local yyyy-mm-dd, so each edge maps to
// its local day boundary — start-of-day / end-of-day — keeping the window
// inclusive.
const toIsoEdge = (ymd: string | undefined, edge: 'start' | 'end'): string | undefined => {
  if (!ymd) return undefined;
  const d = new Date(`${ymd}T${edge === 'start' ? '00:00:00.000' : '23:59:59.999'}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const ActivityLogPage = () => {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  // Custom FROM/TO of the Date-range category (keyed 'created').
  const [dateRanges, setDateRanges] = useState<Record<string, FilterDropdownDateRange>>({});
  /** Single expanded row (accordion) — null when all are collapsed. */
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // User filter options — fetched up-front so the pane is ready on first open.
  const { data: userOptions = [] } = useGetUsersSelectionQuery();

  // "Any time" → undefined (never on the wire); the option list never offers
  // last_90_days (the backend takes none), so the narrowing cast is sound.
  const created = dateFilterOf(filters, 'created') as
    | Exclude<FormDateFilter, FormDateFilter.LAST_90_DAYS>
    | undefined;

  const { data, currentData, isLoading, isFetching, isError } = useGetActivityLogsQuery(
    {
      search: debouncedSearch || undefined,
      module: filters.module.size ? [...filters.module] : undefined,
      // The set's ids originate from the enum-keyed ACTION_META, so the cast is
      // sound.
      action: filters.action.size ? ([...filters.action] as ActivityLogAction[]) : undefined,
      userId: filters.user.size ? [...filters.user] : undefined,
      created,
      createdStart: toIsoEdge(dateRangeOf(dateRanges, 'created', created, 'start'), 'start'),
      createdEnd: toIsoEdge(dateRangeOf(dateRanges, 'created', created, 'end'), 'end'),
      page,
      limit,
    },
    { refetchOnMountOrArgChange: true },
  );

  // A new search or filter narrows the set — snap back to page 1 so the pager
  // can't sit past the end of the new list.
  useEffect(() => setPage(1), [debouncedSearch, filters, dateRanges]);
  // Collapse on any dataset change — the open row may not be in the new page.
  useEffect(() => setExpandedId(null), [debouncedSearch, filters, dateRanges, page, limit]);

  const toggleExpand = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  // One combined Filters trigger (the app-wide FilterDropdown idiom) — the
  // Module / User / Action categories share its rail.
  const filterCategories: FilterDropdownCategory[] = useMemo(
    () => [
      {
        key: 'module',
        label: t('activity_log_filter_module'),
        icon: 'layout-grid',
        options: MODULE_SLUGS.map((slug) => ({ id: slug, label: categoryLabel(slug, t) })),
      },
      {
        key: 'user',
        label: t('activity_log_filter_user'),
        icon: 'user',
        searchable: true,
        searchPlaceholder: t('members_search_people'),
        options: userOptions.map((u) => ({ id: u.id, label: u.name })),
      },
      {
        key: 'action',
        label: t('activity_log_filter_action'),
        icon: 'activity',
        options: Object.entries(ACTION_META).map(([slug, meta]) => ({
          id: slug,
          label: t(meta.labelKey),
        })),
      },
      {
        key: 'created',
        label: t('activity_log_filter_date'),
        icon: 'calendar',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        // The shared preset list minus last_90_days — this wire doesn't take it.
        options: buildDateFilterOptions(t).filter((o) => o.id !== FormDateFilter.LAST_90_DAYS),
      },
    ],
    [t, userOptions],
  );
  const columns = useActivityColumns();

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className='activity-log'>
      <header className='activity-log__header'>
        <div>
          <h1 className='activity-log__title'>{t('activity_log_title')}</h1>
          <p className='activity-log__subtitle'>{t('activity_log_subtitle')}</p>
        </div>
      </header>

      <div className='activity-log__toolbar'>
        <div className='activity-log__toolbar-lead'>
          <div className='activity-log__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('activity_log_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='activity-log__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('activity_log_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>

          <FilterDropdown
            label={t('action_filter')}
            categories={filterCategories}
            value={filters}
            ranges={dateRanges}
            onApply={(next, ranges) => {
              setFilters(next);
              setDateRanges(ranges);
            }}
            emptyText={t('text_no_results_found')}
          />
        </div>

        <span className='activity-log__count'>
          {t('activity_log_entries_count', { count: total })}
        </span>
      </div>

      <Card padded={false} className='activity-log__panel'>
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(a) => a.id}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={900}
          onRowClick={(row) => toggleExpand(row.id)}
          renderExpanded={(row) => <ActivityDetailPanel item={row} />}
          expandedId={expandedId}
          onToggleExpand={toggleExpand}
          expandLabel={t('activity_log_expand_row')}
          empty={
            <>
              <Icon name='scroll-text' size={26} />
              {t('activity_log_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('activity_log_items_per_page')}
          rangeText={t('activity_log_range', { from, to, total })}
        />
      </Card>
    </div>
  );
};

export default ActivityLogPage;
